import { requestJson } from './http';

export class VoiceCall {
  constructor(update, receive) {
    this.update = update; this.receive = receive; this.closed = false;
    this.id = crypto.randomUUID();
    this.controller = new AbortController();
    this.audio = new Audio(); this.audio.autoplay = true; this.audio.playsInline = true;
  }

  async start(language, settings = {}) {
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia || !window.RTCPeerConnection) throw new Error('unsupported');
    this.update({ status: 'requesting' });
    window.dispatchEvent(new CustomEvent('lawyah-call-starting', { detail: this.id }));
    this.connectTimer = setTimeout(() => this.fail('timeout'), 45000);
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    if (this.closed) { this.stream.getTracks().forEach(track => track.stop()); return; }
    this.update({ status: 'connecting' });
    this.ticket = await requestJson('/voice/token', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ language, consent: true, ...settings }), signal: this.controller.signal });
    if (this.closed) { this.endRemote(); return; }
    this.update({ sessionId: this.ticket.session_id, remaining: this.ticket.max_seconds });
    clearTimeout(this.connectTimer);
    this.connectTimer = setTimeout(() => this.fail('timeout'), this.ticket.connection_timeout_seconds * 1000);
    const peer = new RTCPeerConnection(); this.peer = peer;
    this.stream.getTracks().forEach(track => { peer.addTrack(track, this.stream); track.onended = () => this.fail('microphone'); });
    peer.ontrack = event => { this.audio.srcObject = event.streams[0] || new MediaStream([event.track]); this.play(); };
    peer.onconnectionstatechange = () => {
      clearTimeout(this.disconnectTimer);
      if (peer.connectionState === 'failed') this.fail('disconnected');
      if (peer.connectionState === 'disconnected') this.disconnectTimer = setTimeout(() => this.fail('disconnected'), 5000);
    };
    const channel = peer.createDataChannel('oai-events'); this.channel = channel;
    channel.onmessage = event => this.handleMessage(event.data);
    channel.onclose = () => { if (!this.closed) this.fail('disconnected'); };
    channel.onopen = () => {
      if (this.closed) return;
      clearTimeout(this.connectTimer); this.update({ status: 'connected' });
      this.send({ type: 'response.create' });
    };
    const offer = await peer.createOffer(); await peer.setLocalDescription(offer);
    const answer = await this.request('/connect', { sdp: offer.sdp }, { signal: this.controller.signal });
    if (this.closed) { this.endRemote(); return; }
    this.endsAt = Date.parse(answer.ends_at);
    await peer.setRemoteDescription({ type: 'answer', sdp: answer.sdp });
    if (this.closed) return;
    this.clock = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((this.endsAt - Date.now()) / 1000));
      this.update({ remaining });
      if (!remaining) { this.close(); this.update({ status: 'ended', reason: 'limit', speaking: false, thinking: false, muted: false }); }
    }, 1000);
  }

  request(path, body, options = {}) {
    return requestJson(`/voice/${this.ticket.session_id}${path}`, { method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Call-Capability': this.ticket.capability },
      body: JSON.stringify(body || {}), ...options });
  }

  handleMessage(data) {
    if (this.closed) return;
    let event;
    try { event = JSON.parse(data); }
    catch { console.warn('[Lawyah voice] Discarded malformed event; payload not logged.'); return; }
    if (!event || typeof event.type !== 'string') {
      console.warn('[Lawyah voice] Discarded invalid event envelope; payload not logged.'); return;
    }
    try { this.receive(event); }
    catch {
      // Never expose transcript content, SDP or capabilities in diagnostics.
      console.error('[Lawyah voice] Event handling failed; ending the call safely.');
      this.fail('voice_unavailable');
    }
  }

  send(event) { if (this.channel?.readyState === 'open') this.channel.send(JSON.stringify(event)); }
  mute(muted) { this.stream?.getAudioTracks().forEach(track => { track.enabled = !muted; }); }
  interrupt(generating) { if (generating) this.send({ type: 'response.cancel' }); this.send({ type: 'output_audio_buffer.clear' }); }
  play() { this.audio.play().then(() => this.update({ playbackBlocked: false })).catch(() => this.update({ playbackBlocked: true })); }
  fail(error) { if (!this.closed) { this.close(); this.update({ status: 'error', error, speaking: false, thinking: false, muted: false }); } }
  endRemote() {
    if (this.ticket) this.request('/end', {}, { keepalive: true }).catch(() => {
      console.warn('[Lawyah voice] Remote hangup could not be confirmed; server cleanup remains scheduled.');
    });
  }

  close() {
    if (this.closed) return;
    this.closed = true; this.controller.abort();
    [this.connectTimer, this.disconnectTimer].forEach(clearTimeout); clearInterval(this.clock);
    this.stream?.getTracks().forEach(track => { track.onended = null; track.stop(); });
    if (this.channel) { this.channel.onclose = null; this.channel.onmessage = null; this.channel.close(); }
    if (this.peer) { this.peer.onconnectionstatechange = null; this.peer.ontrack = null; this.peer.close(); }
    this.audio.pause(); this.audio.srcObject = null; this.endRemote();
  }
}